import { useFormik, useFormikContext } from 'formik';
import { t } from 'i18next';
import { FieldInput, FieldNumberFormat, FormCard, toastFailure } from 'motion-components';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Modal, ModalProps, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaMinus, FaPen, FaPlus } from 'react-icons/fa';
import { createModal } from 'react-modal-promise';
import { ButtonCloseModal } from 'shared-pages/components';
import { roundToDecimalPlaces } from './rounding';

function calculateContributionItemDetailPrice(values, detail) {
  const unitPrice = values.unitPrice.unitPrice;
  const qty1 = parseFloat(detail.qty1);
  const qty3 = parseFloat(detail.qty3);
  const newPrice = !Number.isNaN(qty1) && !Number.isNaN(qty3) ? qty1 * qty3 * unitPrice : undefined;

  return Number.isNaN(newPrice) || newPrice === undefined ? null : roundToDecimalPlaces(newPrice);
}

/** Έλεγχος ότι η γραμμή έχει συμπληρωμένα όλα τα πεδία ποσότητας */
function isDetailRowFilled(detail) {
  return Boolean(detail?.qty1 && detail?.qty2 && detail?.qty3);
}

type Props = {
  readonly open: boolean;
  readonly close: (result: any) => void;
  readonly title: string;
  /** body of dialog */
  readonly collectorId?: number;
  readonly data?: any;
} & ModalProps;

const ModalInput = ({ open, close, collectorId, data, title, isFormEditable, itemIdx }: Props) => {
  return (
    <Modal size="xl" show={open} centered backdrop="static">
      <CntItemDetailsFormPage
        title={title}
        data={data}
        modalOnClose={(formValues) => {
          close(formValues);
        }}
        isFormEditable={isFormEditable}
        itemIdx={itemIdx}
      />
    </Modal>
  );
};
export const promiseModalCntItemDetailsInput = createModal(ModalInput);
// ------------------------------------------------------------------------------------------------------

// data: ContributionItems

const CntItemDetailsFormPage = ({ title, data, modalOnClose, isFormEditable, itemIdx }) => {
  const initialValues =
    !data.cntContributionItemDetails ||
    (data.cntContributionItemDetails && data.cntContributionItemDetails.length === 0)
      ? {
          ...data,
          cntContributionItemDetails: [
            {
              aa: 1,
              qty1: null,
              qty2: null,
              qty3: null,
              price: null,
            },
          ],
        }
      : data;

  return (
    <FormCard
      title={title}
      onSubmit={() => {}}
      initialValues={initialValues}
      injectFooterElements={(formikCtx) => {
        // close modal
        return (
          <ButtonCloseModal
            onClick={() => {
              // Validate data here;
              if (isFormEditable) {
                // Διέγραψε Κενές Γραμμές
                formikCtx.values.cntContributionItemDetails =
                  formikCtx.values.cntContributionItemDetails.filter((detail) => {
                    if (!detail.qty1 && !detail.qty2 && !detail.qty3) return false;
                    else return true;
                  });

                const details = formikCtx.values.cntContributionItemDetails;
                // Validate
                let ok = true;

                for (let index = 0; index < details.length; index++) {
                  const detail = details[index];
                  if (!detail.qty1) ok = false;
                  if (!detail.qty2) ok = false;
                  if (!detail.qty3) ok = false;
                  // Επίτρεψε κενά στοιχεία
                  if (!detail.qty1 && !detail.qty2 && !detail.qty3) ok = true;
                }
                if (!ok) {
                  toastFailure('Λάθος! Συμπληρώστε όλα τα πεδία');
                  return;
                }
              }
              // Close Modal
              modalOnClose(formikCtx.values);
            }}
          />
        );
      }}
      noButtons={true}
    >
      <div className="d-flex flex-column" style={{ minHeight: '60vh' }}>
        <CntItemDetailsFormFields isFormEditable={isFormEditable} />
      </div>
    </FormCard>
  );
};

// ----------------------------------------------------------------------------------------------------
const CntItemDetailsFormFields = ({ isFormEditable }) => {
  const { t } = useTranslation();
  const frmkCntx = useFormikContext();
  const { values } = frmkCntx;

  // Υπολογισμός Calculated Fields (Force Re-Render)
  const [idxTouched, setIdxTouched] = useState(-1);
  const [recalculateLoadedValues, setRecalculateLoadedValues] = useState(true);
  const handleQtyChange = useCallback(
    (fieldName, idx, value) => {
      // Τα values του formik δεν έχουν τιμές εδώ και
      // για αυτό φτιάχτηκε ξεχωριστό effect
      setIdxTouched(idx);
    },
    [setIdxTouched],
  );

  useEffect(() => {
    if (!(values && (values as any)?.cntContributionItemDetails)) return;
    if (!recalculateLoadedValues || idxTouched >= 0) return;

    const idxToRecalculate = (values as any).cntContributionItemDetails.findIndex((detail) => {
      const calculatedPrice = calculateContributionItemDetailPrice(values, detail);
      const currentPrice =
        detail.price === null || detail.price === undefined ? null : Number(detail.price);

      return calculatedPrice !== currentPrice;
    });

    if (idxToRecalculate >= 0) setIdxTouched(idxToRecalculate);
    else setRecalculateLoadedValues(false);
  }, [values, idxTouched, recalculateLoadedValues]);

  /**
   * Υπολόγισε αξία γραμμής και αθροίσματα — μόνο όταν αλλάζει idxTouched,
   * όχι σε κάθε αλλαγή values (αποφεύγεται infinite loop με setFieldValue).
   */
  useEffect(() => {
    if (idxTouched < 0) return;

    const idx = idxTouched;
    setIdxTouched(-1);

    const currentValues = frmkCntx.values as any;
    if (!currentValues?.cntContributionItemDetails?.[idx]) return;

    const lcValues = {
      ...currentValues,
      cntContributionItemDetails: [...currentValues.cntContributionItemDetails],
    };

    const detail = currentValues.cntContributionItemDetails[idx];
    const unitPrice = currentValues.unitPrice.unitPrice;
    const calculatedPrice = calculateContributionItemDetailPrice(currentValues, detail);
    const currentPrice =
      detail.price === null || detail.price === undefined || detail.price === ''
        ? null
        : roundToDecimalPlaces(Number(detail.price));

    if (calculatedPrice !== currentPrice) {
      frmkCntx.setFieldValue(`cntContributionItemDetails[${idx}].price`, calculatedPrice);
    }
    lcValues.cntContributionItemDetails[idx] = {
      ...lcValues.cntContributionItemDetails[idx],
      price: calculatedPrice,
    };

    const newTotal = lcValues.cntContributionItemDetails.reduce(
      (acc, el) => {
        return {
          qty1: acc.qty1 + (el.qty1 ? parseFloat(el.qty1) : 0),
          qty2: acc.qty2 + (el.qty2 ? parseFloat(el.qty2) : 0),
          qty3:
            acc.qty3 + (el.qty1 ? parseFloat(el.qty1) : 0) * (el.qty3 ? parseFloat(el.qty3) : 0),
          price: acc.price + (el.price ? parseFloat(el.price) : 0),
        };
      },
      { qty1: 0, qty2: 0, qty3: 0, price: 0 },
    );

    const newQty3 =
      newTotal.qty1 > 0
        ? parseFloat(String(newTotal.price)) / unitPrice / parseFloat(String(newTotal.qty1))
        : 0;

    if (currentValues.qty1 !== newTotal.qty1) frmkCntx.setFieldValue(`qty1`, newTotal.qty1);
    if (currentValues.qty2 !== newTotal.qty2) frmkCntx.setFieldValue(`qty2`, newTotal.qty2);
    if (currentValues.qty3 !== newQty3) frmkCntx.setFieldValue(`qty3`, newQty3);
    if (currentValues.price !== newTotal.price) frmkCntx.setFieldValue(`price`, newTotal.price);
  }, [idxTouched, frmkCntx]);

  /**
   * Προσθήκη νέας γραμμής — μόνο αν η τρέχουσα (τελευταία) είναι πλήρης
   */
  function addNewDetail(): void {
    const details = [...((frmkCntx.values as any).cntContributionItemDetails ?? [])];
    const last = details[details.length - 1];
    if (!isDetailRowFilled(last)) {
      toastFailure('Συμπληρώστε πρώτα όλα τα πεδία της τρέχουσας γραμμής');
      return;
    }
    details.push({
      aa: details.length + 1,
      qty1: null,
      qty2: null,
      qty3: null,
      price: null,
    });
    frmkCntx.setFieldValue('cntContributionItemDetails', details);
  }

  /**
   * Διαγραφή γραμμής — αμετάβλητο αντίγραφο πίνακα, μετά επανυπολογισμός συνόλων
   */
  function deleteDetail(idx: number): void {
    const details = [...((frmkCntx.values as any).cntContributionItemDetails ?? [])];

    if (details.length <= 1) {
      frmkCntx.setFieldValue('cntContributionItemDetails', [
        { aa: 1, qty1: null, qty2: null, qty3: null, price: null },
      ]);
      setIdxTouched(0);
      return;
    }

    details.splice(idx, 1);
    details.forEach((row, i) => {
      row.aa = i + 1;
    });
    frmkCntx.setFieldValue('cntContributionItemDetails', details);
    setIdxTouched(Math.min(idx, details.length - 1));
  }

  const details = (values as any).cntContributionItemDetails ?? [];
  const lastDetail = details[details.length - 1];
  const canAddNewLine = isFormEditable && isDetailRowFilled(lastDetail);

  /* Render
   */
  const unitPriceLabel = 'Εισφορά ανά Τεμάχιο';
  return (
    <>
      {(values as any).cntContributionItemDetails &&
        (values as any).cntContributionItemDetails.map((el, idx, arr) => {
          return (
            <Row key={`cnt-detail-row-${idx}`} className="m-0 p-0 flex-nowrap align-items-end">
              <Col className="col-auto align-self-center" style={{ width: '60px' }}>
                <strong>{idx + 1}</strong>:
              </Col>
              <FieldNumberFormat
                key={`qty1_${idx + 1}:`}
                disabled={!isFormEditable}
                as={Col}
                label={t((values as any)?.item.units1)}
                name={`cntContributionItemDetails[${idx}].qty1`}
                decimalScale={0}
                placeholder=""
                onChange={(e) => handleQtyChange('qty1', idx, e.target.value)}
              />
              <FieldNumberFormat
                key={`qty3_${idx}`}
                disabled={!isFormEditable}
                as={Col}
                label={t((values as any)?.item.units3)}
                name={`cntContributionItemDetails[${idx}].qty3`}
                placeholder=""
                onChange={(e) => handleQtyChange('qty3', idx, e.target.value)}
              />
              {/* Ποσότητα 2 */}
              <FieldNumberFormat
                key={`qty2_${idx}`}
                disabled={!isFormEditable}
                as={Col}
                label={t((values as any)?.item.units2)}
                name={`cntContributionItemDetails[${idx}].qty2`}
                placeholder=""
                onChange={(e) => handleQtyChange('qty2', idx, e.target.value)}
              />
              {/* Unit Price */}
              <FieldNumberFormat
                key={`unitprice_${idx}`}
                decimalScale={3}
                disabled={true}
                as={Col}
                label={t(unitPriceLabel)}
                name={`unitPrice.unitPrice`}
                placeholder=""
                suffixLabel="€"
              />
              {/* Total Price */}
              <FieldNumberFormat
                key={`price_${idx}`}
                disabled={true}
                as={Col}
                label={t('Αξία')}
                name={`cntContributionItemDetails[${idx}].price`}
                placeholder=""
                suffixLabel="€"
              />
              {/* Κουμπιά +/- στην ίδια γραμμή (τελευταία σειρά: και προσθήκη) */}
              <Col
                className="col-auto d-flex flex-nowrap align-items-center gap-1 flex-shrink-0"
                style={{ width: '76px' }}
              >
                {idx + 1 === arr.length && (
                  <Button
                    className="btn-circle btn-success"
                    variant="primary"
                    data-toggle="tooltip"
                    data-placement="left"
                    title={t('Νέα Γραμμή')}
                    onClick={addNewDetail}
                    disabled={!canAddNewLine}
                  >
                    <FaPlus className="mb-1" />
                  </Button>
                )}
                <Button
                  className="btn-circle btn-danger"
                  variant="primary"
                  data-toggle="tooltip"
                  data-placement="left"
                  title={t('Διαγραφή Γραμμής')}
                  onClick={() => deleteDetail(idx)}
                  disabled={!isFormEditable}
                >
                  <FaMinus className="mb-1" />
                </Button>
              </Col>
            </Row>
          );
        })}
      {/* Total */}
      {/* <hr className="mt-6"></hr> */}
      <Row className="m-0 pt-2 mt-auto" style={{ marginTop: 'auto', background: 'lightgray' }}>
        <Col className="col-auto align-self-center" style={{ width: '60px' }}>
          {t('Σύνολο')}
        </Col>
        <FieldNumberFormat
          key={`qty1`}
          disabled={true}
          as={Col}
          label={t((values as any)?.item.units1)}
          name={`qty1`}
          placeholder=""
        />
        <FieldNumberFormat
          key={`qty3`}
          disabled={true}
          as={Col}
          label={t((values as any)?.item.units3)}
          name={`qty3`}
          placeholder=""
        />
        {/* Ποσότητα 2 */}
        <FieldNumberFormat
          key={`qty2`}
          disabled={true}
          as={Col}
          label={t((values as any)?.item.units2)}
          name={`qty2`}
          placeholder=""
        />
        {/* Unit Price */}
        <FieldNumberFormat
          key={`unitprice`}
          decimalScale={3}
          disabled={true}
          as={Col}
          label={t(unitPriceLabel)}
          name={`unitPrice.unitPrice`}
          placeholder=""
          suffixLabel="€"
        />
        {/* Total Price */}
        <FieldNumberFormat
          key={`price`}
          disabled={true}
          as={Col}
          label={t('Αξία')}
          name={`price`}
          placeholder=""
          suffixLabel="€"
        />
        <Col className="col-auto align-self-center" style={{ width: '71px' }}></Col>
      </Row>
    </>
  );
};

